import { useEffect, useState, useCallback, useRef } from "react";

import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormGroup,
  FormLabel,
  Grid,
  LinearProgress,
  OutlinedInput,
  Typography,
  Tooltip,
} from "@material-ui/core";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import TableProcessamentoCatalogo from "./components/table-processamento-catalogo";
import ModalLogDetail from "./components/modal-log-detail";
import HeaderClientes from "components/_refactor/headers/clients.js";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";

import UtilsFncs from "utils/UtilsFncs";
import Api from "lib/api/httpRequestHelper";
import useLogs from "hooks/logs";
import InputText from "components/_refactor/text/input";

const MAX_FILE_SIZE = 100 * 1024 * 1024;

export default function ImportacaoCatalogoProdutos() {
  const snackbar = useSnackbar();
  const { setLocalStorageApiProblem } = useLogs();
  const xlsxInputRef = useRef(null);
  const pollingTimerRef = useRef(null);

  const [fileState, setFileState] = useState({
    xlsx: { base64: null, name: "", size: 0 },
  });
  const [linkPhotos, setLinkPhotos] = useState("");
  const [errors, setErrors] = useState({ xlsx: null });
  const [isUploading, setIsUploading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [processList, setProcessList] = useState([]);
  const [logDetail, setLogDetail] = useState();

  const validateFile = (file, type) => {
    if (!file) return "Nenhum arquivo selecionado";
    if (file.size > MAX_FILE_SIZE) return "Arquivo muito grande (máximo 100MB)";
    if (
      type === "xlsx" &&
      ![
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ].includes(file.type)
    )
      return "Formato do arquivo inválido! Apenas XLSX é aceito.";
    return null;
  };

  const processFile = useCallback(async (file, type) => {
    const errorMessage = validateFile(file, type);
    if (errorMessage) {
      setErrors((prev) => ({ ...prev, [type]: errorMessage }));
      return;
    }

    try {
      const base64Data = await UtilsFncs.FileToB64(file);
      setFileState((prev) => ({
        ...prev,
        [type]: { base64: base64Data, name: file.name, size: file.size },
      }));
      setErrors((prev) => ({ ...prev, [type]: null }));
    } catch (error) {
      console.error(`Error processing ${type} file:`, error);
      setErrors((prev) => ({
        ...prev,
        [type]: `Erro ao processar arquivo: ${error.message}`,
      }));
    }
  }, []);

  const handleFileChange = useCallback(
    (event, type) => {
      const file = event.target.files[0];
      if (file) {
        processFile(file, type);
      } else {
        setFileState((prev) => ({
          ...prev,
          [type]: { base64: null, name: "", size: 0 },
        }));
      }
    },
    [processFile]
  );

  const resetFileInputs = useCallback(() => {
    if (xlsxInputRef.current) xlsxInputRef.current.value = "";
    setFileState({ xlsx: { base64: null, name: "", size: 0 } });
    setLinkPhotos("");
  }, []);

  const GetProcessListFiles = useCallback(
    async (search) => {
      if (search) setSearching(true);
      else setSearching(false);

      try {
        const response = await Api.get(
          "list-uploads-catalog",
          "trocasDevolucoes",
          "?page=0",
          true
        );

        const formattedProcessList =
          response.Result.ListaProcessamento?.map((item) => ({
            createdAt: item.DataCriadoFormat,
            totalLines: item.TotalLinhas,
            processedLines: item.LinhasProcessadas,
            unprocessedLines: item.LinhaNaoProcessadas,
            status: item.StatusFormat,
            log: item.LogJson,
          })) || [];

        setProcessList(formattedProcessList);
      } catch (error) {
        snackbar.showMessage(
          `Erro ao buscar lista de processamentos: ${error.message}`,
          { severity: "error" }
        );
        setLocalStorageApiProblem({
          idMenuAccess: "",
          screen: "ImportacaoCatalogoProdutos",
          problem: `Erro na chamada do endpoint list-uploads-catalog - ${error}`,
        });
      }
      setSearching(false);
    },
    [snackbar, setLocalStorageApiProblem]
  );

  const uploadCatalog = useCallback(async () => {
    if (!fileState.xlsx.base64) {
      setErrors((prev) => ({ ...prev, xlsx: "Arquivo XLSX é obrigatório" }));
      return;
    }

    if (!linkPhotos) {
      snackbar.showMessage("O link das fotos é obrigatório", {
        severity: "error",
      });
      return;
    }

    setIsUploading(true);

    try {
      const encodedXlsxBase64 = fileState.xlsx.base64
        ?.toString()
        ?.replace(/^data:(.*,)?/, "");

      await Api.post(
        "update-catalog-product",
        "trocasDevolucoes",
        {
          arquivoCsvBase64: encodedXlsxBase64,
          arquivoFotosZIPBase64: linkPhotos,
        },
        true
      );

      await GetProcessListFiles();
      snackbar.showMessage("Catálogo enviado com sucesso!");
      resetFileInputs();
    } catch (error) {
      snackbar.showMessage(error.message || "Erro ao enviar catálogo", {
        severity: "error",
      });
      setLocalStorageApiProblem({
        idMenuAccess: "",
        screen: "ImportacaoCatalogoProdutos",
        problem: `Erro na chamada do endpoint update-catalog-product - ${error}`,
      });
    } finally {
      setIsUploading(false);
    }
  }, [
    fileState,
    linkPhotos,
    GetProcessListFiles,
    snackbar,
    setLocalStorageApiProblem,
    resetFileInputs,
  ]);

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  useEffect(() => {
    GetProcessListFiles(true);
  }, [GetProcessListFiles]);

  useEffect(() => {
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }

    const hasAguardando = processList.some(
      (proc) =>
        proc.status &&
        (proc.status.toUpperCase() === "AGUARDANDO" ||
          proc.status === "PROCESSANDO")
    );

    if (hasAguardando) {
      pollingTimerRef.current = setTimeout(() => {
        GetProcessListFiles();
      }, 3000);
    }

    return () => {
      if (pollingTimerRef.current) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, [processList, GetProcessListFiles]);

  const isSubmitDisabled = !fileState.xlsx.base64 || !linkPhotos || isUploading;
  const submitTooltip = !fileState.xlsx.base64
    ? "Arquivo XLSX é obrigatório"
    : !linkPhotos
    ? "Link das fotos é obrigatório"
    : "";

  return (
    <>
      <HeaderClientes
        section="Importação do catálogo de fotos de produtos"
        subsection="Catálogo de Produtos"
      />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <Grid container>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography
                  variant="h2"
                  style={{
                    marginTop: "1rem",
                    marginBottom: "2rem",
                    color: "#000",
                  }}
                >
                  Envio de arquivos
                </Typography>

                <Grid container spacing={2}>
                  <Grid item sm={6} xs={12}>
                    <FormGroup width="fit-content">
                      <FormLabel
                        style={{
                          textTransform: "uppercase",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Enviar catálogo (.xlsx) *
                      </FormLabel>
                      <OutlinedInput
                        id="uploadXLSX_"
                        fullWidth
                        type="file"
                        inputRef={xlsxInputRef}
                        onChange={(e) => handleFileChange(e, "xlsx")}
                        disabled={isUploading}
                        accept=".xlsx,.xls"
                        error={!!errors.xlsx}
                      />
                      {errors.xlsx && (
                        <Typography color="error" variant="caption">
                          {errors.xlsx}
                        </Typography>
                      )}
                      {fileState.xlsx.name && (
                        <Typography variant="caption">
                          {fileState.xlsx.name} (
                          {formatFileSize(fileState.xlsx.size)})
                        </Typography>
                      )}
                    </FormGroup>
                  </Grid>

                  <Grid item sm={6} xs={12}>
                    <InputText
                      id="uploadZIP_"
                      label="Link das fotos"
                      value={linkPhotos}
                      onChange={(e) => {
                        setLinkPhotos(e.target.value);
                      }}
                      error={!!errors.zip}
                      disabled={isUploading}
                      required
                    />
                  </Grid>
                </Grid>
                <Grid item style={{ marginTop: "20px" }}>
                  <Tooltip title={submitTooltip}>
                    <span>
                      <Button
                        disabled={isSubmitDisabled}
                        variant="contained"
                        color="primary"
                        onClick={uploadCatalog}
                        endIcon={
                          isUploading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <CloudUploadIcon />
                          )
                        }
                      >
                        {isUploading ? "Enviando" : "Enviar"} catálogo
                      </Button>
                    </span>
                  </Tooltip>
                </Grid>
              </CardContent>
            </Card>

            <Grid container>
              <Grid item xs={12}>
                {searching ? (
                  <div>
                    <span>
                      Buscando listas de catálogos em processamento...
                    </span>
                    <LinearProgress />
                  </div>
                ) : (
                  <TableProcessamentoCatalogo
                    data={processList}
                    setLogDetail={setLogDetail}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      {logDetail && (
        <ModalLogDetail
          log={logDetail}
          handleClose={() => setLogDetail(null)}
        />
      )}
    </>
  );
}
